import React from 'react';
import { Search, Lock } from 'lucide-react';
import type { TierDraft } from '../../services/packageTiers';

/**
 * "Features by tier" (approved board 2, /packages tier editor) — the ONE place a
 * package's features are chosen. One row per feature, one column per tier:
 *   - the top column (Advanced) IS the package's own feature list (`topFeatures`);
 *   - Basic / Pro columns are the lower tier drafts (shown only when the package
 *     has tier drafts, i.e. editing an existing package).
 * With "builds up" on, ticking a lower tier ticks every tier above it, and
 * unticking a tier unticks every tier below it — so a higher tier always holds
 * the lower one.
 */
export interface MatrixFeature { id: string; name?: string; category?: string; status?: string }

const RUBY = '#DC143C';
const RUBY_DARK = '#9F1239';

export const FeaturesByTier: React.FC<{
  features: MatrixFeature[];
  categoryLabel: (c: string) => string;
  categoryRank: (c: string) => number;
  /** The top tier's (= the package's) features. */
  topFeatures: string[];
  onTopFeatures: (next: string[]) => void;
  /** Lower tiers (rank 1, 2) — null when the package has no tiers (create). */
  drafts: TierDraft[] | null;
  onDrafts: (next: TierDraft[]) => void;
  /** featureId → name of another package (outside this family) that owns it. */
  ownedElsewhere: Record<string, string>;
  loading?: boolean;
}> = ({ features, categoryLabel, categoryRank, topFeatures, onTopFeatures, drafts, onDrafts, ownedElsewhere, loading }) => {
  const [buildsUp, setBuildsUp] = React.useState(true);
  const [query, setQuery] = React.useState('');
  const [onlyInPackage, setOnlyInPackage] = React.useState(topFeatures.length > 0);

  const top = new Set(topFeatures);
  const lower = drafts ? drafts.filter((d) => d.rank < 3) : [];
  const columns: Array<{ rank: 1 | 2 | 3; label: string; on: (f: string) => boolean; sell: boolean; color: string }> = [
    ...lower.map((d) => ({ rank: d.rank, label: d.label || (d.rank === 1 ? 'Basic' : 'Pro'), on: (f: string) => d.features.includes(f), sell: d.sell, color: d.rank === 1 ? '#57534e' : RUBY_DARK })),
    { rank: 3, label: drafts ? (drafts.find((d) => d.rank === 3)?.label || 'Advanced') : 'Included', on: (f: string) => top.has(f), sell: true, color: RUBY },
  ];

  const toggle = (rank: 1 | 2 | 3, f: string) => {
    const col = columns.find((c) => c.rank === rank)!;
    const turnOn = !col.on(f);
    // Which ranks change: just this one, or (builds up) this one and every rank
    // above when turning on / below when turning off.
    const hits = (r: number) => r === rank || (buildsUp && (turnOn ? r > rank : r < rank));
    if (hits(3)) {
      const next = new Set(topFeatures);
      turnOn ? next.add(f) : next.delete(f);
      onTopFeatures([...next]);
    }
    if (drafts) {
      onDrafts(drafts.map((d) => {
        if (d.rank === 3 || !hits(d.rank) || (!d.sell && d.rank !== rank)) return d;
        const s = new Set(d.features);
        turnOn ? s.add(f) : s.delete(f);
        return { ...d, features: [...s] };
      }));
    }
  };

  const inPackage = (f: string) => top.has(f) || lower.some((d) => d.features.includes(f));
  const q = query.trim().toLowerCase();
  const visible = features
    .filter((f) => !onlyInPackage || inPackage(f.id))
    .filter((f) => !q || f.id.toLowerCase().includes(q) || (f.name || '').toLowerCase().includes(q) || categoryLabel(f.category || '').toLowerCase().includes(q))
    .sort((a, b) => categoryRank(a.category || '') - categoryRank(b.category || '') || (a.name || a.id).localeCompare(b.name || b.id));

  let lastGroup = '';
  const totals = columns.map((c) => features.filter((f) => c.on(f.id)).length);

  return (
    <section className="bg-white border border-stone-200 rounded-xl overflow-hidden" aria-labelledby="fbt-h">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-stone-200">
        <div>
          <h2 id="fbt-h" className="text-sm font-semibold text-stone-900">{drafts ? 'Features by tier' : 'Features'}</h2>
          <p className="text-xs text-stone-500">
            {drafts
              ? 'Tick a feature in a tier. With "builds up" on, a higher tier always includes the lower one.'
              : 'Tick every feature this package includes.'}
          </p>
        </div>
        {drafts && (
          <button
            type="button"
            role="switch"
            aria-checked={buildsUp}
            onClick={() => setBuildsUp((v) => !v)}
            className="min-h-[36px] flex items-center gap-2 text-xs font-medium text-stone-700"
          >
            Each tier builds on the one below
            <span className={`relative w-9 h-5 rounded-full transition-colors ${buildsUp ? 'bg-[#DC143C]' : 'bg-stone-300'}`}>
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${buildsUp ? 'left-[18px]' : 'left-0.5'}`} />
            </span>
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-stone-100 bg-stone-50">
        <label className="relative flex-1 min-w-[220px]">
          <span className="sr-only">Search features</span>
          <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search features…"
            className="w-full h-9 pl-9 pr-3 rounded-lg border border-stone-300 bg-white text-sm focus:outline-none focus:border-stone-500"
          />
        </label>
        <div className="flex rounded-lg border border-stone-300 bg-white p-0.5" role="group" aria-label="Which features to show">
          {[
            { v: true, label: 'In this package' },
            { v: false, label: 'All features' },
          ].map((o) => (
            <button
              key={o.label}
              type="button"
              aria-pressed={onlyInPackage === o.v}
              onClick={() => setOnlyInPackage(o.v)}
              className={`h-8 px-3 rounded-md text-xs font-medium ${onlyInPackage === o.v ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-100'}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm min-w-[560px]">
          <thead>
            <tr className="bg-stone-50 text-left">
              <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-stone-500">Feature</th>
              {columns.map((c) => (
                <th key={c.rank} className="px-2 py-2 w-[104px] text-center text-xs font-semibold" style={{ color: c.color }}>
                  {c.label}
                  {!c.sell && <span className="block text-[11px] font-normal text-stone-500">Not sold</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={columns.length + 1} className="px-5 py-8 text-center text-stone-500">Loading features…</td></tr>
            )}
            {!loading && visible.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} className="px-5 py-8 text-center text-stone-500">
                  {onlyInPackage && !q ? 'No features yet — switch to "All features" to add some.' : 'No feature matches that search.'}
                </td>
              </tr>
            )}
            {!loading && visible.map((f) => {
              const group = categoryLabel(f.category || '');
              const showGroup = group !== lastGroup;
              lastGroup = group;
              const owner = ownedElsewhere[f.id];
              const comingSoon = f.status === 'coming_soon';
              const locked = !!owner || comingSoon;
              return (
                <React.Fragment key={f.id}>
                  {showGroup && (
                    <tr>
                      <td colSpan={columns.length + 1} className="px-4 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wider text-stone-400 border-t border-stone-100 bg-stone-50/60">
                        {group}
                      </td>
                    </tr>
                  )}
                  <tr className="border-t border-stone-100">
                    <td className="px-4 py-1">
                      <div className="text-sm font-medium text-stone-900 flex items-center gap-2 flex-wrap">
                        {f.name || f.id}
                        {owner && <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-600"><Lock className="w-3 h-3" aria-hidden="true" />{owner}</span>}
                        {comingSoon && <span className="text-[11px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-500">Coming soon</span>}
                      </div>
                      <div className="font-mono text-[11px] text-stone-400">{f.id}</div>
                    </td>
                    {columns.map((c) => {
                      const on = c.on(f.id);
                      const disabled = locked || !c.sell;
                      return (
                        <td key={c.rank} className="text-center px-2 py-1">
                          <button
                            type="button"
                            aria-pressed={on}
                            aria-label={`${c.label}: ${f.name || f.id}`}
                            disabled={disabled}
                            title={owner ? `Included in ${owner}` : comingSoon ? 'Coming soon' : !c.sell ? 'This tier is not sold' : undefined}
                            onClick={() => toggle(c.rank, f.id)}
                            className="w-9 h-9 rounded-lg border inline-grid place-items-center transition-colors disabled:opacity-35 disabled:cursor-not-allowed"
                            style={on ? { background: c.color, borderColor: c.color } : { background: '#fff', borderColor: '#d6d3d1' }}
                          >
                            {on && (
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" aria-hidden="true"><path d="M5 12l5 5 9-10" /></svg>
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="bg-stone-50 border-t border-stone-200 font-bold">
              <td className="px-4 py-2 text-xs uppercase tracking-wide text-stone-500">Total</td>
              {columns.map((c, i) => (
                <td key={c.rank} className="text-center" style={{ color: c.color }}>{totals[i]}</td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
};
